import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../services/axiosConfig';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Parse JWT token and extract user info
  const parseToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  };

  // Check if the token is valid and not expired
  const isTokenValid = (token) => {
    if (!token) return false;

    try {
      const decodedToken = parseToken(token);
      if (!decodedToken || !decodedToken.exp) return false;

      const currentTime = Math.floor(Date.now() / 1000);
      return decodedToken.exp > currentTime;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  };

  // Init - Check if user is already logged in via token
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Set the token in axios headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Parse the token to get user info
          const decodedToken = parseToken(token);
          if (decodedToken) {
            // Check if token is expired
            const currentTime = Math.floor(Date.now() / 1000);
            if (decodedToken.exp && decodedToken.exp < currentTime) {
              // Token is expired
              console.log('Token expired, redirecting to login');
              localStorage.removeItem('token');

              // Save current location before redirecting
              if (window.location.pathname !== '/login') {
                localStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
              }
            } else {
              // Map the token payload to our user object
              setUser({
                userId: decodedToken.id,
                role: decodedToken.role,
                email: decodedToken.email,
                username: decodedToken.username,
                firstName: decodedToken.firstName,
                lastName: decodedToken.lastName
              });
            }
          }
        } catch (error) {
          console.error('Authentication error:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    // Listen for unauthorized events from axios interceptor
    const handleUnauthorized = (event) => {
      console.log('Unauthorized access:', event.detail?.message);
      setError(event.detail?.message || 'Session expired');
      setUser(null);
    };

    window.addEventListener('unauthorizedAccess', handleUnauthorized);
    initAuth();

    return () => {
      window.removeEventListener('unauthorizedAccess', handleUnauthorized);
    };
  }, []);

  // Login function
  const login = async (identifier, password) => {
    try {
      setError(null);
      // Determine if identifier is email or username
      const isEmail = identifier.includes('@');
      const credentials = isEmail 
        ? { email: identifier, password } 
        : { username: identifier, password };
      const response = await axios.post('/auth/login', credentials);
      const { token } = response.data;

      // Save token to localStorage
      localStorage.setItem('token', token);

      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Parse token to get user info
      const decodedToken = parseToken(token);

      // Set user state
      setUser({
        userId: decodedToken.id,
        role: decodedToken.role,
        email: decodedToken.email,
        username: decodedToken.username
      });

      // Redirect after successful login
      redirectAfterLogin();

      return true;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please check your credentials.');
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Only call the backend if we have a token
      const token = localStorage.getItem('token');
      if (token) {
        // Call the logout endpoint to invalidate the token
        await axios.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clean up local state even if the API call fails
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      navigate('/login');
    }
  };

  // Check if user has specific role
  const hasRole = (requiredRoles) => {
    if (!user) return false;
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(user.role);
    }
    return user.role === requiredRoles;
  };

  // Redirect after login based on user role or saved path
  const redirectAfterLogin = () => {
    if (!user) return;

    // Check if there's a saved path to redirect to
    const savedPath = localStorage.getItem('redirectAfterLogin');

    if (savedPath) {
      // Remove the saved path to avoid future unwanted redirects
      localStorage.removeItem('redirectAfterLogin');
      navigate(savedPath);
      return;
    }

    // Default role-based redirects if no saved path
    switch (user.role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'tutor':
        navigate('/homepage');
        break;
      case 'doctor':
        navigate('/');  // Main DocList view
        break;
      default:
        navigate('/');
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    hasRole,
    redirectAfterLogin,
    isTokenValid
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
